import React, { useEffect, useState, useContext } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import EventRow from "../components/EventRow";
import { OperaContext } from "../context/OperaContext";
import { formatEventDates } from "../utils/dateUtils";
import { SeasonNoticeAndNewsletter } from "../components/SeasonNoticeAndNewsletter";
import Breadcrumb from "../components/Breadcrumb";
import { useTranslation } from "react-i18next";

function PerformerPage() {
  const { performerName } = useParams();
  const { t } = useTranslation();
  // Helper to normalize URLs to absolute
  function normalizeUrl(url) {
    if (!url) return undefined;
    if (url.startsWith("http://") || url.startsWith("https://")) return url;
    if (url.startsWith("/")) return `https://operatoday.org${url}`;
    return `https://operatoday.org/${url}`;
  }
  const { loading } = useContext(OperaContext);
  const [performer, setPerformer] = useState(null);
  const [events, setEvents] = useState([]);
  const [loadingPerformer, setLoadingPerformer] = useState(true);
  const [error, setError] = useState(null);

  // Format name with decorative styling
  const formatPerformerName = (name) => {
    if (!name) return null;
    return name.split(" ").map((word, wordIndex) => (
      <span key={wordIndex}>
        <span className="font-cinzel-important">{word.charAt(0)}</span>
        <span className="font-cinzel-important">{word.slice(1)}</span>
        {wordIndex < name.split(" ").length - 1 && " "}
      </span>
    ));
  };
  // Smart image positioning based on aspect ratio
  const handleSmartImageLoad = (e) => {
    const img = e.target;
    const ratio = img.naturalWidth / img.naturalHeight;

    // Only apply special positioning for tall portrait images (ratio < 1)
    if (ratio < 1) {
      const className = img.className;
      if (className.includes("h-[600px]") || className.includes("h-full")) {
        img.style.objectPosition = "center 8%"; // Hero images
      } else if (className.includes("h-[154px]")) {
        img.style.objectPosition = "center 5%"; // Thumbnails
      }
    }
    // Square and landscape images keep default center positioning
  };

  useEffect(() => {
    if (loading) return;

    setLoadingPerformer(true);

    // Fetch performer by slug directly
    axios
      .get(`/api/famous-performers/${performerName}`)
      .then((res) => {
        setPerformer(res.data);
        return axios
          .get(`/api/events?performer=${res.data._id}`)
          .then((eventsRes) => {
            setEvents(eventsRes.data);
          });
      })
      .catch((err) => {
        setError(err.message || "An error occurred");
      })
      .finally(() => {
        setLoadingPerformer(false);
      });
  }, [performerName, loading]);

  // Show loading state while context is loading or performer data is being fetched
  if (loading || loadingPerformer) {
    return (
      <div className="container mx-auto text-center text-black py-20">
        {t("PerformersPage.loading")}
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mx-auto text-center text-black py-20">
        {t("PerformersPage.error")}: {error}
      </div>
    );
  }

  if (!performer) {
    return (
      <div className="container mx-auto text-center text-black py-20">
        {t("PerformersPage.performerNotFound")}
      </div>
    );
  }

  const heroImage = performer.coverImage || performer.image || "/images/default-performer.jpg";

  return (
    <div className="bg-white">
      {/* Schema.org Person JSON-LD for Performer */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{
        __html: JSON.stringify(
          (() => {
            const data = {
              "@context": "https://schema.org",
              "@type": "Person",
              "name": performer.name,
            };
            if (performer.image) data.image = normalizeUrl(performer.image);
            if (performer.description) data.description = performer.description;
            return data;
          })()
        )
      }} />
      <div className="relative w-full h-[600px] overflow-hidden">
        <img
          src={heroImage}
          alt={performer.name}
          className="w-full h-full object-cover"
          onLoad={handleSmartImageLoad}
        />
      </div>
      <Breadcrumb
        items={[
          { href: "/", label: "Home" },
          {
            href: "/performers",
            label: "Performers",
          },
          {
            href: "#",
            label: performer.name,
          },
        ]}
      />
      <main className="max-w-[1280px] mx-auto py-8 px-4">
        <h1 className="text-3xl text-left mb-4 capitalize">
          {performer.name}
        </h1>
        <div className="flex flex-col md:flex-row md:justify-between md:gap-8 mb-8" >
          <div className="text-lg text-black leading-relaxed md:flex-1 text-justify md:w-[77%]" dangerouslySetInnerHTML={{ __html: performer?.description }} />
          <div className="hidden md:block md:flex-1 md:max-w-[22%]">
            {performer.image ? (
              <img
                src={performer.image}
                alt={performer.name}
                className="w-[260px] h-[260px] object-cover"
                onLoad={handleSmartImageLoad}
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.style.display = "none";
                }}
              />
            ) : (
              <div className="w-[260px] h-[260px] bg-gray-100 flex items-center justify-center text-gray-500 text-base rounded-lg"></div>
            )}
          </div>
        </div>
        <section>
          <h2 className="text-2xl text-left mb-4">
            {t("PerformersPage.upcomingPerformances")}
          </h2>
          {events.length === 0 ? (
            <p className="text-black text-center italic py-8">
              {t("PerformersPage.hero")}
            </p>
          ) : (
            <div className="flex flex-col gap-2 max-w-[1240px] mx-auto my-6">
              {events?.map((event) => (
                <EventRow
                  key={event._id}
                  event={event}
                  formatEventDates={formatEventDates}
                  page="performer"
                  pageImage={performer?.image}
                />
              ))}
            </div>
          )}
          <SeasonNoticeAndNewsletter />
        </section>
      </main>
    </div>
  );
}

export default PerformerPage;
